'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Button, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Popover, PopoverContent, PopoverTrigger, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components';
import { ChevronRight, Eye, Plus } from '@repo/ui/icons';
import type { StyleWithCssVariables } from '@repo/ui/lib/css-variables';
import { type KeyboardEvent, useId, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { groupByCategory } from './column-meta.ts';
import { PaneTitle } from './components/pane-title.tsx';
import { revealColumn } from './components/reveal-column.ts';
import { isSelectionColumn } from './components/selection-column';
import { useNewColumnIds, useTableState } from './context/table-state-provider.tsx';
import { type TableColumnExamples, useColumnExamples } from './hooks/use-column-examples.ts';
import { type Column, flexRender, type RowData, type TableColumnGroup } from './tanstack-table';
type ColumnLabel = <TData extends RowData>(column: Column<TData, unknown>) => string;

// Command takes every Enter for its highlighted column and cancels the key, so a pane with
// buttons keeps Enter from it, and each button does what it says.
const keepEnter = (event: KeyboardEvent) => {
  if (event.key === 'Enter') event.stopPropagation();
};
const groupColumns = <TData extends RowData,>(columns: Column<TData, unknown>[]) => groupByCategory(columns, column => column.columnDef.meta?.group).map(({
  group,
  items
}) => ({
  columns: items,
  group
}));
export const AddColumnMenu = t0 => {
  const $ = _c(139);
  const {
    announce,
    examples,
    getLabel
  } = t0;
  const {
    columnOrder,
    setColumnOrder,
    table,
    tableRef
  } = useTableState(_temp);
  const newColumnIds = useNewColumnIds();
  const [search, setSearch] = useState("");
  const [groupLabel, setGroupLabel] = useState("");
  const [highlightedId, setHighlightedId] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [addedIds, setAddedIds] = useState(t1);
  const [lastAdd, setLastAdd] = useState(null);
  const searchRef = useRef(null);
  const allShownId = useId();
  let T0;
  let T1;
  let T2;
  let T3;
  let addHighlighted;
  let highlighted;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t2;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let undoLastAdd;
  if ($[1] !== addedIds || $[2] !== allShownId || $[3] !== announce || $[4] !== columnOrder || $[5] !== examples || $[6] !== getLabel || $[7] !== groupLabel || $[8] !== highlightedId || $[9] !== lastAdd || $[10] !== newColumnIds || $[11] !== search || $[12] !== setColumnOrder || $[13] !== table || $[14] !== tableRef) {
    const hideableColumns = table.getAllLeafColumns().filter(_temp2);
    const allShown = hideableColumns.every(_temp3);
    let t30;
    if ($[50] !== addedIds || $[51] !== groupLabel) {
      t30 = entry_0 => entry_0.columns.length > 0 || addedIds.length > 0 && entry_0.group.label === groupLabel;
      $[50] = addedIds;
      $[51] = groupLabel;
      $[52] = t30;
    } else {
      t30 = $[52];
    }
    const groups = groupColumns(hideableColumns).map(_temp5).filter(t30);
    const query = search.trim().toLocaleLowerCase();
    const matches = (column_2, group) => [getLabel(column_2), group.label].some(label => label.toLocaleLowerCase().includes(query));
    const chosen = groups.find(entry_1 => entry_1.group.label === groupLabel) ?? groups[0];
    const listed = query ? groups.map(entry_2 => ({
      ...entry_2,
      columns: entry_2.columns.filter(column_3 => matches(column_3, entry_2.group))
    })).filter(_temp6) : groups.filter(entry_4 => entry_4 === chosen);
    const emptied = !query && chosen?.columns.length === 0 ? chosen.group : undefined;
    const listedColumns = listed.flatMap(_temp7);
    let t31;
    if ($[53] !== highlightedId) {
      t31 = column_4 => column_4.id === highlightedId;
      $[53] = highlightedId;
      $[54] = t31;
    } else {
      t31 = $[54];
    }
    highlighted = listedColumns.find(t31);
    addHighlighted = () => {
      if (!highlighted) {
        return;
      }
      const label_0 = getLabel(highlighted);
      flushSync(() => setHighlightedId(""));
      if (chosen) {
        setGroupLabel(chosen.group.label);
      }
      const order = table.getAllLeafColumns().map(_temp8);
      setColumnOrder([...order.filter(id => id !== highlighted.id), highlighted.id]);
      highlighted.toggleVisibility(true);
      setAddedIds(ids => [...ids, highlighted.id]);
      setLastAdd({
        columnOrder,
        id: highlighted.id,
        label: label_0
      });
      announce(`${label_0} added to the end of the table`);
    };
    let t32;
    if ($[55] !== announce || $[56] !== lastAdd || $[57] !== setColumnOrder || $[58] !== table) {
      t32 = () => {
        if (!lastAdd) {
          return;
        }
        setColumnOrder(lastAdd.columnOrder);
        table.getColumn(lastAdd.id)?.toggleVisibility(false);
        setAddedIds(_temp9);
        setLastAdd(null);
        setHighlightedId(lastAdd.id);
        announce(`${lastAdd.label} removed`);
        searchRef.current?.focus();
      };
      $[55] = announce;
      $[56] = lastAdd;
      $[57] = setColumnOrder;
      $[58] = table;
      $[59] = t32;
    } else {
      t32 = $[59];
    }
    undoLastAdd = t32;
    T3 = Popover;
    if ($[60] !== addedIds || $[61] !== examples || $[62] !== tableRef) {
      t25 = open => {
        setSearch("");
        examples?.onMenuOpenChange?.(open);
        if (open) {
          return;
        }
        const revealId = addedIds.at(-1);
        if (revealId && tableRef.current) {
          revealColumn(tableRef.current, revealId);
        }
        setAddedIds([]);
        setLastAdd(null);
      };
      $[60] = addedIds;
      $[61] = examples;
      $[62] = tableRef;
      $[63] = t25;
    } else {
      t25 = $[63];
    }
    t26 = "Popover";
    t27 = "AddColumnMenu";
    t28 = "settings-add-column.tsx";
    const t33 = allShown ? 0 : undefined;
    let t34;
    if ($[64] !== t33) {
      t34 = <span className="inline-flex" tabIndex={t33} />;
      $[64] = t33;
      $[65] = t34;
    } else {
      t34 = $[65];
    }
    let t35;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = <Button variant="outline" />;
      $[66] = t35;
    } else {
      t35 = $[66];
    }
    let t36;
    if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = <Plus data-sentry-element="Plus" data-sentry-source-file="settings-add-column.tsx" />;
      $[67] = t36;
    } else {
      t36 = $[67];
    }
    let t37;
    if ($[68] !== allShownId) {
      t37 = <span hidden={true} id={allShownId}>All columns are shown</span>;
      $[68] = allShownId;
      $[69] = t37;
    } else {
      t37 = $[69];
    }
    const t38 = <TooltipTrigger disabled={!allShown} render={t34} data-sentry-element="TooltipTrigger" data-sentry-source-file="settings-add-column.tsx"><PopoverTrigger aria-describedby={allShown ? allShownId : undefined} disabled={allShown} render={t35} data-sentry-element="PopoverTrigger" data-sentry-source-file="settings-add-column.tsx">{t36} Add column</PopoverTrigger>{t37}</TooltipTrigger>;
    let t39;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t39 = <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="settings-add-column.tsx">All columns are shown</TooltipContent>;
      $[70] = t39;
    } else {
      t39 = $[70];
    }
    if ($[71] !== t38) {
      t29 = <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="settings-add-column.tsx">{t38}{t39}</Tooltip>;
      $[71] = t38;
      $[72] = t29;
    } else {
      t29 = $[72];
    }
    T2 = PopoverContent;
    t21 = "end";
    t22 = "w-[min(52rem,calc(100vw-2rem))] p-0";
    t23 = "PopoverContent";
    t24 = "settings-add-column.tsx";
    T1 = Command;
    t12 = true;
    t13 = "Search hidden columns";
    t14 = event => {
      if (event.key === "Enter" && !event.nativeEvent.isComposing) {
        addHighlighted();
      }
    };
    t15 = setHighlightedId;
    t16 = false;
    t17 = highlightedId;
    t18 = "Command";
    t19 = "settings-add-column.tsx";
    if ($[73] !== search) {
      t20 = <CommandInput onValueChange={setSearch} placeholder={"Search columns\u2026"} ref={searchRef} value={search} data-sentry-element="CommandInput" data-sentry-source-file="settings-add-column.tsx" />;
      $[73] = search;
      $[74] = t20;
    } else {
      t20 = $[74];
    }
    t10 = "mt-1 flex h-100 border-t";
    let t40;
    if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
      t40 = <PaneTitle data-sentry-element="PaneTitle" data-sentry-source-file="settings-add-column.tsx">Group</PaneTitle>;
      $[75] = t40;
    } else {
      t40 = $[75];
    }
    t11 = <nav aria-label="Column groups" className="w-64 shrink-0 overflow-y-auto bg-muted py-1 pl-1 shadow-[inset_-1px_0_var(--color-border)]" onKeyDown={keepEnter}>{t40}{groups.map(t41 => {
        const {
          columns,
          group: group_0
        } = t41;
        return <button aria-pressed={!query && group_0.label === chosen?.group.label} className="group/group flex w-full items-center gap-2 rounded-l-sm px-2 py-1.5 text-left text-sm text-muted-foreground outline-none hover:bg-popover/50 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset aria-pressed:bg-popover aria-pressed:text-foreground" key={group_0.label} onClick={() => {
          setSearch("");
          setGroupLabel(group_0.label);
          setHighlightedId(columns[0]?.id ?? "");
        }} type="button"><span className="flex-1 truncate">{group_0.label}</span>{" "}<span className="text-xs tabular-nums">{columns.length}</span><ChevronRight aria-hidden={true} className="size-3.5 opacity-0 group-aria-pressed/group:opacity-60" /></button>;
      })}</nav>;
    t7 = "flex w-60 shrink-0 flex-col shadow-[inset_-1px_0_var(--color-border)]";
    if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <PaneTitle className="px-3" data-sentry-element="PaneTitle" data-sentry-source-file="settings-add-column.tsx">Columns</PaneTitle>;
      $[76] = t8;
    } else {
      t8 = $[76];
    }
    t9 = emptied ? <p className="px-3 py-6 text-center text-sm text-muted-foreground">All {emptied.label} columns added.</p> : null;
    T0 = CommandList;
    t2 = "max-h-none flex-1 pb-1 pl-1";
    t3 = "CommandList";
    t4 = "settings-add-column.tsx";
    t5 = emptied ? null : <CommandEmpty>No hidden columns match.</CommandEmpty>;
    t6 = listed.map(t42 => {
      const {
        columns: columns_0,
        group: group_1
      } = t42;
      return <CommandGroup className="pr-0" heading={query ? group_1.label : undefined} key={group_1.label}>{columns_0.map(column_6 => <CommandItem className="rounded-l-sm rounded-r-none data-[selected=true]:bg-muted [&>svg:last-child]:hidden" key={column_6.id} value={column_6.id}>{getLabel(column_6)}{newColumnIds.has(column_6.id) && <Badge variant="secondary">New</Badge>}</CommandItem>)}</CommandGroup>;
    });
    $[1] = addedIds;
    $[2] = allShownId;
    $[3] = announce;
    $[4] = columnOrder;
    $[5] = examples;
    $[6] = getLabel;
    $[7] = groupLabel;
    $[8] = highlightedId;
    $[9] = lastAdd;
    $[10] = newColumnIds;
    $[11] = search;
    $[12] = setColumnOrder;
    $[13] = table;
    $[14] = tableRef;
    $[15] = T0;
    $[16] = T1;
    $[17] = T2;
    $[18] = T3;
    $[19] = addHighlighted;
    $[20] = highlighted;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
    $[30] = t19;
    $[31] = t2;
    $[32] = t20;
    $[33] = t21;
    $[34] = t22;
    $[35] = t23;
    $[36] = t24;
    $[37] = t25;
    $[38] = t26;
    $[39] = t27;
    $[40] = t28;
    $[41] = t29;
    $[42] = t3;
    $[43] = t4;
    $[44] = t5;
    $[45] = t6;
    $[46] = t7;
    $[47] = t8;
    $[48] = t9;
    $[49] = undoLastAdd;
  } else {
    T0 = $[15];
    T1 = $[16];
    T2 = $[17];
    T3 = $[18];
    addHighlighted = $[19];
    highlighted = $[20];
    t10 = $[21];
    t11 = $[22];
    t12 = $[23];
    t13 = $[24];
    t14 = $[25];
    t15 = $[26];
    t16 = $[27];
    t17 = $[28];
    t18 = $[29];
    t19 = $[30];
    t2 = $[31];
    t20 = $[32];
    t21 = $[33];
    t22 = $[34];
    t23 = $[35];
    t24 = $[36];
    t25 = $[37];
    t26 = $[38];
    t27 = $[39];
    t28 = $[40];
    t29 = $[41];
    t3 = $[42];
    t4 = $[43];
    t5 = $[44];
    t6 = $[45];
    t7 = $[46];
    t8 = $[47];
    t9 = $[48];
    undoLastAdd = $[49];
  }
  let t30;
  if ($[77] !== T0 || $[78] !== t2 || $[79] !== t3 || $[80] !== t4 || $[81] !== t5 || $[82] !== t6) {
    t30 = <T0 className={t2} data-sentry-element={t3} data-sentry-source-file={t4}>{t5}{t6}</T0>;
    $[77] = T0;
    $[78] = t2;
    $[79] = t3;
    $[80] = t4;
    $[81] = t5;
    $[82] = t6;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  let t31;
  if ($[84] !== t30 || $[85] !== t7 || $[86] !== t8 || $[87] !== t9) {
    t31 = <div className={t7}>{t8}{t9}{t30}</div>;
    $[84] = t30;
    $[85] = t7;
    $[86] = t8;
    $[87] = t9;
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  let t32;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <PaneTitle className="px-0" data-sentry-element="PaneTitle" data-sentry-source-file="settings-add-column.tsx">Preview</PaneTitle>;
    $[89] = t32;
  } else {
    t32 = $[89];
  }
  let t33;
  if ($[90] !== examples || $[91] !== getLabel || $[92] !== highlighted) {
    t33 = <div className="flex-1 overflow-y-auto px-3 pb-3">{t32}{highlighted ? <ColumnPreview column={highlighted} examples={examples} getLabel={getLabel} /> : <p className="text-sm text-muted-foreground">Choose a column to preview it.</p>}</div>;
    $[90] = examples;
    $[91] = getLabel;
    $[92] = highlighted;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  let t34;
  if ($[94] !== lastAdd || $[95] !== undoLastAdd) {
    t34 = lastAdd ? <p className="mb-2 flex items-center gap-2 text-xs text-muted-foreground"><span className="min-w-0 flex-1 truncate">{lastAdd.label} added to the end of the table</span><Button onClick={undoLastAdd} size="xs" variant="ghost">Undo</Button></p> : null;
    $[94] = lastAdd;
    $[95] = undoLastAdd;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  const t35 = !highlighted;
  let t36;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Plus data-sentry-element="Plus" data-sentry-source-file="settings-add-column.tsx" />;
    $[97] = t36;
  } else {
    t36 = $[97];
  }
  let t37;
  if ($[98] !== addHighlighted || $[99] !== t35) {
    t37 = <Button className="w-full" disabled={t35} onClick={addHighlighted} data-sentry-element="Button" data-sentry-source-file="settings-add-column.tsx">{t36} Add to table</Button>;
    $[98] = addHighlighted;
    $[99] = t35;
    $[100] = t37;
  } else {
    t37 = $[100];
  }
  let t38;
  if ($[101] !== t34 || $[102] !== t37) {
    t38 = <div className="border-t p-3" onKeyDown={keepEnter}>{t34}{t37}</div>;
    $[101] = t34;
    $[102] = t37;
    $[103] = t38;
  } else {
    t38 = $[103];
  }
  let t39;
  if ($[104] !== t33 || $[105] !== t38) {
    t39 = <div className="flex min-w-0 flex-1 flex-col bg-muted">{t33}{t38}</div>;
    $[104] = t33;
    $[105] = t38;
    $[106] = t39;
  } else {
    t39 = $[106];
  }
  let t40;
  if ($[107] !== t10 || $[108] !== t11 || $[109] !== t31 || $[110] !== t39) {
    t40 = <div className={t10}>{t11}{t31}{t39}</div>;
    $[107] = t10;
    $[108] = t11;
    $[109] = t31;
    $[110] = t39;
    $[111] = t40;
  } else {
    t40 = $[111];
  }
  let t41;
  if ($[112] !== T1 || $[113] !== t12 || $[114] !== t13 || $[115] !== t14 || $[116] !== t15 || $[117] !== t16 || $[118] !== t17 || $[119] !== t18 || $[120] !== t19 || $[121] !== t20 || $[122] !== t40) {
    t41 = <T1 disablePointerSelection={t12} label={t13} onKeyDown={t14} onValueChange={t15} shouldFilter={t16} value={t17} data-sentry-element={t18} data-sentry-source-file={t19}>{t20}{t40}</T1>;
    $[112] = T1;
    $[113] = t12;
    $[114] = t13;
    $[115] = t14;
    $[116] = t15;
    $[117] = t16;
    $[118] = t17;
    $[119] = t18;
    $[120] = t19;
    $[121] = t20;
    $[122] = t40;
    $[123] = t41;
  } else {
    t41 = $[123];
  }
  let t42;
  if ($[124] !== T2 || $[125] !== t21 || $[126] !== t22 || $[127] !== t23 || $[128] !== t24 || $[129] !== t41) {
    t42 = <T2 align={t21} className={t22} data-sentry-element={t23} data-sentry-source-file={t24}>{t41}</T2>;
    $[124] = T2;
    $[125] = t21;
    $[126] = t22;
    $[127] = t23;
    $[128] = t24;
    $[129] = t41;
    $[130] = t42;
  } else {
    t42 = $[130];
  }
  let t43;
  if ($[131] !== T3 || $[132] !== t25 || $[133] !== t26 || $[134] !== t27 || $[135] !== t28 || $[136] !== t29 || $[137] !== t42) {
    t43 = <T3 onOpenChange={t25} data-sentry-element={t26} data-sentry-component={t27} data-sentry-source-file={t28}>{t29}{t42}</T3>;
    $[131] = T3;
    $[132] = t25;
    $[133] = t26;
    $[134] = t27;
    $[135] = t28;
    $[136] = t29;
    $[137] = t42;
    $[138] = t43;
  } else {
    t43 = $[138];
  }
  return t43;
};

/**
 * The column drawn as the table draws it, with up to three rows that have a value: rows on
 * this page first, then `examples.moreRows`, and last an example row marked Example.
 */
const ColumnPreview = t0 => {
  const $ = _c(27);
  const {
    column,
    examples,
    getLabel
  } = t0;
  const {
    isExample,
    rows,
    source
  } = useColumnExamples(column, examples);
  const description = column.columnDef.meta?.description;
  const tableRef = useRef(null);
  const [cellText, setCellText] = useState("");
  let t1;
  if ($[0] !== column) {
    t1 = column.getSize();
    $[0] = column;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `${t1}px`;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      "--column-width": t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const columnWidthStyle = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const cells = [...(tableRef.current?.querySelectorAll("td") ?? [])];
      setCellText(cells.map(_temp10).join("; "));
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useLayoutEffect(t4);
  let t5;
  if ($[5] !== description) {
    t5 = description ? <p className="text-sm">{description}</p> : null;
    $[5] = description;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Eye aria-hidden={true} className="mt-px size-3.5 shrink-0" data-sentry-element="Eye" data-sentry-source-file="settings-add-column.tsx" />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== source) {
    t7 = <p className="flex items-start gap-1.5 text-xs text-muted-foreground">{t6}<span>{source}</span></p>;
    $[8] = source;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== column || $[11] !== columnWidthStyle || $[12] !== getLabel || $[13] !== isExample || $[14] !== rows) {
    t8 = rows.length > 0 ? <table className="pointer-events-none w-full table-fixed text-sm" inert={true} ref={tableRef}><thead><tr><th className="h-8 px-2 text-left text-xs font-medium text-muted-foreground"><span className="flex items-center gap-2"><span className="truncate">{getLabel(column)}</span>{isExample ? <Badge variant="secondary">Example</Badge> : null}</span></th></tr></thead><tbody>{rows.map(row => {
          const cell_0 = row.getAllCellsByColumnId()[column.id];
          return <tr key={row.id}><td className="min-h-10 min-w-0 border-t p-2 align-middle" style={columnWidthStyle}>{cell_0 ? flexRender(cell_0.column.columnDef.cell, cell_0.getContext()) : null}</td></tr>;
        })}</tbody></table> : null;
    $[10] = column;
    $[11] = columnWidthStyle;
    $[12] = getLabel;
    $[13] = isExample;
    $[14] = rows;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== cellText || $[17] !== column || $[18] !== getLabel) {
    t9 = cellText ? <p className="sr-only">{`${getLabel(column)}: ${cellText}`}</p> : null;
    $[16] = cellText;
    $[17] = column;
    $[18] = getLabel;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t10 = <section aria-label="Sample data preview" className="flex flex-col gap-1.5 rounded-md border border-dashed border-foreground/15 p-2">{t7}{t8}{t9}</section>;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t5) {
    t11 = <div className="flex cursor-default flex-col gap-2 select-none" data-sentry-component="ColumnPreview" data-sentry-source-file="settings-add-column.tsx">{t5}{t10}</div>;
    $[24] = t10;
    $[25] = t5;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
function _temp(state) {
  return {
    columnOrder: state.columnOrder,
    columnVisibility: state.columnVisibility
  };
}
function _temp2(column) {
  return !isSelectionColumn(column.id) && column.getCanHide();
}
function _temp3(column_0) {
  return column_0.getIsVisible();
}
function _temp4(column_1) {
  return !column_1.getIsVisible();
}
function _temp5(entry) {
  return {
    ...entry,
    columns: entry.columns.filter(_temp4)
  };
}
function _temp6(entry_3) {
  return entry_3.columns.length > 0;
}
function _temp7(entry_5) {
  return entry_5.columns;
}
function _temp8(column_5) {
  return column_5.id;
}
function _temp9(ids_0) {
  return ids_0.slice(0, -1);
}
function _temp10(cell) {
  return cell.innerText.trim().replaceAll("\n", ", ");
}